<template>
    <common-dropdown :isOpen="isOpen" arrowPositionY="top" arrowPositionX="left">
        <section class="appointment-calendar-slots">
            <common-loader :isProcessing="isSending"></common-loader>
            <schedule-24-warning-modal
                v-if="is24WarningOpen"
                :physician="physician"
                :practice="practice"
                :location="location"
                @close="close24Warning()"
            ></schedule-24-warning-modal>

            <template v-if="isSent">
                <header class="appointment-calendar-slots__header">
                    <common-date
                        :date="date"
                        :copy="$tc('pages.referrals.new.steps.schedule.slots_available', slots.length)"
                    ></common-date>
                </header>

                <div
                    ref="options"
                    class="appointment-calendar-slots__content"
                    :class="{ 'appointment-calendar-slots__content--pool': isReferralPool }"
                >
                    <section v-if="amOptions.length" class="appointment-calendar-slots__group">
                        <h5 class="appointment-calendar-slots__time-of-day">
                            {{ $t('pages.referrals.new.steps.schedule.am') }}
                        </h5>

                        <common-content-spacer v-if="isReferralPool" height="tiny">
                            <checkbox-input
                                v-for="(slot, index) in amOptions"
                                :key="index"
                                :isDisabled="
                                    totalSlotCount > 2 && !selectedSlots.some((selectedSlot) => isequal(selectedSlot, slot))
                                "
                                :label="slot.label"
                                :value="checked(slot)"
                                @change="selectMultipleSlots(slot)"
                            ></checkbox-input>
                        </common-content-spacer>

                        <radio-input
                            v-else
                            class="appointment-calendar-slots__group--radio"
                            :value="selectedSlot"
                            :options="amOptions"
                            direction="column"
                            checkedIcon="radio-check"
                            @change="selectSlot($event)"
                        ></radio-input>
                    </section>

                    <section v-if="pmOptions.length" class="appointment-calendar-slots__group">
                        <h5 class="appointment-calendar-slots__time-of-day">
                            {{ $t('pages.referrals.new.steps.schedule.pm') }}
                        </h5>

                        <common-content-spacer v-if="isReferralPool" height="tiny">
                            <checkbox-input
                                v-for="(slot, index) in pmOptions"
                                :key="index"
                                :isDisabled="
                                    totalSlotCount > 2 && !selectedSlots.some((selectedSlot) => isequal(selectedSlot, slot))
                                "
                                :label="slot.label"
                                :value="checked(slot)"
                                @change="selectMultipleSlots(slot)"
                            ></checkbox-input>
                        </common-content-spacer>

                        <radio-input
                            v-else
                            :value="selectedSlot"
                            :options="pmOptions"
                            direction="column"
                            checkedIcon="radio-check"
                            @change="selectSlot($event)"
                        ></radio-input>
                    </section>
                </div>

                <footer v-if="!isReferralPool" class="appointment-calendar-slots__footer">
                    <template v-if="selectedSlot">
                        <div class="appointment-calendar-slots__details">
                            {{ mapSlotToOption(selectedSlot).label }}
                        </div>
                    </template>

                    <div class="appointment-calendar-slots__submit">
                        <common-button
                            :label="$t(`pages.referrals.new.steps.schedule.${buttonType}`)"
                            :isDisabled="!selectedSlot"
                            type="primary"
                            size="large"
                            @click="confirmSlot()"
                        ></common-button>
                    </div>
                </footer>
            </template>
        </section>
    </common-dropdown>
</template>

<script>
    import dayjs from 'dayjs'
    import UsersAPI from 'api/UsersAPI'
    import PracticesAPI from 'api/PracticesAPI'
    import CalendarSlotsMixin from 'mixins/CalendarSlotsMixin'
    import RadioInput from 'components/form-inputs/RadioInput'
    import CheckboxInput from 'components/form-inputs/CheckboxInput'
    import Schedule24WarningModal from 'components/page-elements/referrals-new-schedule/Schedule24WarningModal'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import isequal from 'lodash.isequal'
    import emitter from 'tiny-emitter/instance'
    import DemoReferral from 'services/DemoReferral'

    export default {
        mixins: [CalendarSlotsMixin],

        components: {
            RadioInput,
            CheckboxInput,
            Schedule24WarningModal,
        },

        props: {
            physician: { type: Object, required: true },
            practice: { type: Object, required: true },
            location: { type: Object, required: true },
            selectedSlotsByDate: { type: Object, required: false, default: undefined },
        },

        inject: {
            isReferralPool: {
                default: false,
            },
            sharedState: {},
        },

        data() {
            return {
                slots: [],
                selectedSlot: null,
                selectedSlots: [],
                is24WarningOpen: false,
                isequal: isequal,
            }
        },

        computed: {
            buttonType() {
                return this.$route.params.action === 'update-slot' ? 'confirm_slot' : 'request_slot'
            },

            totalSlotCount() {
                return this.sharedState.totalSlotCount
            },

            amOptions() {
                return this.slots.filter(({ to }) => to <= 12 * 60).map(this.mapSlotToOption)
            },

            pmOptions() {
                return this.slots.filter(({ from }) => from >= 12 * 60).map(this.mapSlotToOption)
            },
        },

        methods: {
            selectSlot(slot) {
                if (slot && this.date.add(slot.from, 'minutes').isBefore(dayjs().add(1, 'day'))) {
                    return (this.is24WarningOpen = true)
                }
                this.selectedSlot = slot
            },
            selectMultipleSlots(slot) {
                if (slot && this.date.add(slot.value.from, 'minutes').isBefore(dayjs().add(1, 'day'))) {
                    return (this.is24WarningOpen = true)
                }

                const index = this.selectedSlots.findIndex((s) => isequal(s, slot))
                if (index !== -1) {
                    this.selectedSlots.splice(index, 1)
                } else {
                    this.selectedSlots.push(slot)
                }
                this.emitEvent({
                    name: 'selectSlot',
                    payload: {
                        slots: this.selectedSlots,
                        date: this.date,
                    },
                })
            },
            checked(slot) {
                return this.selectedSlots.some((s) => isequal(s, slot))
            },

            close24Warning() {
                this.is24WarningOpen = false
            },

            confirmSlot() {
                this.emitEvent({
                    name: 'selectSlot',
                    payload: {
                        slot: this.selectedSlot,
                        date: this.date,
                    },
                })
            },

            mapSlotToOption(slot) {
                let start = dayjs().startOf('day').add(slot.from, 'minutes').format('H:mm')
                let end = dayjs().startOf('day').add(slot.to, 'minutes').format('H:mm')
                if (start.length < 5) {
                    start = '0' + start
                }
                if (end.length < 5) {
                    end = '0' + end
                }
                return {
                    label: [start, end].join(' – '),
                    value: slot,
                }
            },
        },

        watch: {
            isOpen() {
                if (!this.isOpen || this.slots.length) {
                    return
                }

                const request = {
                    year: this.date.format('YYYY'),
                    month: this.date.format('M'),
                    day: this.date.format('D'),
                    practiceId: this.isReferralPool ? this.$route.query.practiceId : this.practice?.id,
                    practiceLocationId: this.isReferralPool ? this.$route.query.practiceLocationId : this.location.id,
                }

                if (this.$auth.isUserPracticeDemo()) {
                    this.slots = DemoReferral.getSlots().map((slot) => ({
                        ...slot,
                        date: dayjs(this.date).format('YYYY-MM-DD'),
                    }))
                    this.isSending = false
                    this.isSent = true
                    this.$validation.clear()
                    this.indicateScrollability()
                    return false
                }

                if (this.$route.query.practiceType === PracticeTypeEnum.ANCILLARY) {
                    this.handleRequest(PracticesAPI.getAvailableSlots(this.$route.query.practiceId, request)).then((slots) => {
                        this.slots = slots
                        this.indicateScrollability()
                    })
                } else {
                    const userId = this.isReferralPool
                        ? this.$auth.getActiveMember()?.id
                            ? this.$auth.getActiveMember()?.id
                            : this.$auth.getUser().id
                        : this.physician.id
                    this.handleRequest(UsersAPI.getAvailableSlots(userId, request)).then((slots) => {
                        this.slots = slots
                        this.indicateScrollability()
                        if (this.isReferralPool) {
                            const formattedDate = dayjs(this.date).format('YYYY-MM-DD')

                            let foundSlots = []

                            Object.values(this.selectedSlotsByDate).forEach((slots) => {
                                const filteredSlots = slots.filter((slot) => slot.date === formattedDate)
                                foundSlots.push(...filteredSlots)
                            })

                            if (foundSlots.length > 0) {
                                this.selectedSlots = foundSlots.map((slot) => {
                                    return {
                                        label: slot.label,
                                        value: {
                                            date: slot.date,
                                            endTime: slot.endTime,
                                            from: slot.from,
                                            startTime: slot.startTime,
                                            to: slot.to,
                                        },
                                    }
                                })
                            }
                        }
                    })
                }
            },
        },

        mounted() {
            emitter.on('slot-removed', (payload) => {
                if (dayjs(this.date).utc().format() == dayjs(payload.date).utc().format()) {
                    const slot = {
                        label: payload.slot.label,
                        value: {
                            date: payload.slot.date,
                            endTime: payload.slot.endTime,
                            from: payload.slot.from,
                            startTime: payload.slot.startTime,
                            to: payload.slot.to,
                        },
                    }
                    slot.value
                    this.selectMultipleSlots(slot)
                }
            })
        },

        beforeDestroy() {
            emitter.off('slot-removed')
        },
    }
</script>
